import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Topbar from './components/Topbar';
import MobileHeader from './components/MobileHeader';
import MobileBottomNav from './components/MobileBottomNav';
import Modal from './components/Modal';
import Loader from './components/Loader';
import { fetchReviews, fetchMyReviews, fetchCurrentUser, deleteReview, fetchStats, fetchReviewById, saveToken } from './api';
import LinkPhoneModal from './components/LinkPhoneModal';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Profile from './pages/Profile';

import OnboardingModal from './components/OnboardingModal';
import DashboardTour from './components/DashboardTour';
import ReportsPage from './pages/ReportsPage';
import OfferRatePage from './pages/OfferRatePage';
import GhostRatePage from './pages/GhostRatePage';
import TimelinePage from './pages/TimelinePage';
import AdminPage from './pages/AdminPage';

export default function App() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [user, setUser] = useState(null);
  const [view, setView] = useState('home');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyFilter, setCompanyFilter] = useState('All');
  const [selectedRole, setSelectedRole] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const [widgetRefreshKey, setWidgetRefreshKey] = useState(0);
  const [lastSharedReview, setLastSharedReview] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [flyingName, setFlyingName] = useState(null);
  const [showWelcomePop, setShowWelcomePop] = useState(false);
  const [highlightReviewId, setHighlightReviewId] = useState(null);
  const [showLinkPhone, setShowLinkPhone] = useState(false);
  const searchTimerRef = useRef(null);

  // ── Dark Mode ──
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);
  const toggleDarkMode = () => setDarkMode(d => !d);

  // ── Handle Google OAuth redirect token + Auth Check ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const isNewGoogle = params.get('isNew') === 'true';

    const checkAuth = async () => {
      setAuthLoading(true);
      if (token) {
        saveToken(token);
        window.history.replaceState({}, '', '/');
      }
      const currentUser = await fetchCurrentUser();
      const isAnon = currentUser?.isAnonymous || currentUser?.provider === 'anon';
      setUser(isAnon ? null : currentUser);
      // Show link-phone modal for new Google users
      if (currentUser && !isAnon && isNewGoogle && token) {
        setShowLinkPhone(true);
      }
      setAuthLoading(false);
    };
    checkAuth();
  }, []);

  // ── Platform Stats ── re-fetches on login and after every widget refresh (new submission, etc.)
  useEffect(() => {
    if (!user) return;
    const loadStats = async () => {
      try {
        const data = await fetchStats();
        setPlatformStats(data.stats);
      } catch (err) {
        console.error('Failed to load stats:', err);
      }
    };
    loadStats();
  }, [user, widgetRefreshKey]);

  // Loads the public feed (all users' reviews, filtered/searched)
  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      if (highlightReviewId) {
        // Fetch specific review if we came from a notification
        const data = await fetchReviewById(highlightReviewId);
        setReviews(data.review ? [data.review] : []);
        return;
      }

      const params = { limit: 100 };
      if (searchQuery) params.search = searchQuery;
      const data = await fetchReviews(params);
      let loadedReviews = data.reviews || [];
      if (activeFilter === '👻 Ghosted') loadedReviews = loadedReviews.filter(r => r.status === 'ghosted');
      else if (activeFilter === '✅ Received') loadedReviews = loadedReviews.filter(r => r.status === 'offer');
      else if (activeFilter === '❌ Rejected') loadedReviews = loadedReviews.filter(r => r.status === 'rejected');
      else if (activeFilter === '⏳ In Progress') loadedReviews = loadedReviews.filter(r => r.status === 'ongoing');
      setReviews(loadedReviews);
    } catch (err) {
      console.error('Failed to load reviews:', err);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, searchQuery, highlightReviewId]);

  // Loads only the current user's own kahanis
  const loadMyReviews = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMyReviews();
      let loadedReviews = data.reviews || [];
      if (activeFilter === '👻 Ghosted') loadedReviews = loadedReviews.filter(r => r.status === 'ghosted');
      else if (activeFilter === '✅ Received') loadedReviews = loadedReviews.filter(r => r.status === 'offer');
      else if (activeFilter === '❌ Rejected') loadedReviews = loadedReviews.filter(r => r.status === 'rejected');
      else if (activeFilter === '⏳ In Progress') loadedReviews = loadedReviews.filter(r => r.status === 'ongoing');
      setReviews(loadedReviews);
    } catch (err) {
      console.error('Failed to load my reviews:', err);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  const [autoOpenComments, setAutoOpenComments] = useState(false);
  const handleReviewClick = useCallback((id, openComments = false) => {
    setHighlightReviewId(id);
    setAutoOpenComments(openComments);
    setActiveFilter('All');
    setSearchQuery('');
    setView('highlight');
  }, []);

  const handleHomeClick = useCallback(() => {
    setHighlightReviewId(null);
    setActiveFilter('All');
    setSearchQuery('');
    setView('home');
  }, []);

  useEffect(() => {
    if (!user) return;
    if (view === 'my-reviews' || view === 'my-kahani') loadMyReviews();
    else loadReviews();
  }, [activeFilter, searchQuery, user, view, loadReviews, loadMyReviews, highlightReviewId]);

  // Trigger widget refresh once after user session is confirmed on page load
  useEffect(() => {
    if (user) setWidgetRefreshKey(k => k + 1);
  }, [user]);

  // ── Debounced search handler — switches to search view automatically ──
  const handleSearch = useCallback((rawQuery) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!rawQuery.trim()) {
      setSearchQuery('');
      setHighlightReviewId(null); // Clear highlight on clear search
      setView('home');
      return;
    }
    setHighlightReviewId(null); // Clear individual review focus on new search
    setView('search');
    searchTimerRef.current = setTimeout(() => {
      setSearchQuery(rawQuery.trim());
    }, 350);
  }, []);

  const [showShareGuestPopup, setShowShareGuestPopup] = useState(false);
  const [showJobsGuestPopup, setShowJobsGuestPopup] = useState(false);

  // ── Guarded setView — blocks Jobs for guests ──
  const guardedSetView = (v) => {
    if (v === 'login') { setUser(null); return; }
    if (v === 'jobs' && (user?.isAnonymous || user?.provider === 'anon')) {
      setShowJobsGuestPopup(true);
      return;
    }
    setView(v);
  };

  // ── Guard: only signed-in (non-guest) users can share a kahani ──
  const handleAddClick = () => {
    if (user?.isAnonymous || user?.provider === 'anon') {
      setShowShareGuestPopup(true);
      return;
    }
    setEditingReview(null);
    setIsModalOpen(true);
  };

  // ── Handle delete with backend (Firestore) call ──
  const handleDeleteReview = async (id) => {
    try {
      await deleteReview(id);
      setReviews(prev => prev.filter(r => (r._id || r.id) !== id));
    } catch (err) {
      console.error('Failed to delete review:', err);
    }
  };

  if (authLoading) {
    return <Loader message="Verifying Identity..." />;
  }

  // ── Login screen ──
  if (!user) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login onLogin={(u, isNew) => { setUser(u); if (isNew) setShowOnboarding(true); }} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <style>{`* { box-sizing: border-box; } body { margin: 0; }`}</style>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      {/* Desktop Topbar */}
      <Topbar
        onSearch={handleSearch}
        searchQuery={searchQuery}
        onAddClick={handleAddClick}
        user={user}
        setUser={setUser}
        setView={guardedSetView}
        totalKahanis={platformStats?.totalReviews}
        onReviewClick={handleReviewClick}
        onHomeClick={handleHomeClick}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
      />

      {/* Mobile Header */}
      <MobileHeader
        onSearch={handleSearch}
        searchQuery={searchQuery}
        user={user}
        setUser={setUser}
        setView={guardedSetView}
        view={view}
        onReviewClick={handleReviewClick}
        onHomeClick={handleHomeClick}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
      />

      <div className="app-body-with-mobile-header">
        <Routes>
          <Route
            path="/"
            element={
              <Home
                reviews={reviews}
                loading={loading}
                activeFilter={activeFilter}
                setActiveFilter={setActiveFilter}
                searchQuery={searchQuery}
                onSearch={handleSearch}
                onAddClick={handleAddClick}
                onEditClick={(r) => { setEditingReview(r); setIsModalOpen(true); }}
                onDeleteClick={handleDeleteReview}
                user={user}
                view={view}
                setView={guardedSetView}
                onHomeClick={handleHomeClick}
                selectedCompany={selectedCompany}
                setSelectedCompany={(name, filter = 'All') => {
                  setSelectedCompany(name);
                  setCompanyFilter(filter);
                }}
                companyFilter={companyFilter}
                setCompanyFilter={setCompanyFilter}
                selectedRole={selectedRole}
                setSelectedRole={setSelectedRole}
                totalKahanis={platformStats?.totalReviews}
                widgetRefreshKey={widgetRefreshKey}
                lastSharedReview={lastSharedReview}
                onReviewClick={handleReviewClick}
                autoOpenComments={autoOpenComments}
              />
            }
          />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/profile" element={<Profile user={user} onLogout={() => setUser(null)} onUserUpdate={(updates) => setUser(u => ({ ...u, ...updates }))} />} />
          <Route path="/jobs" element={<Navigate to="/" replace />} />
          <Route path="/companies" element={<Navigate to="/" replace />} />
          <Route path="/salaries" element={<Navigate to="/" replace />} />

          {/* Stat Detail Pages */}
          <Route path="/reports" element={<ReportsPage user={user} view={view} setView={setView} setSelectedCompany={setSelectedCompany} refreshSignal={reviews} />} />
          <Route path="/offer-rate" element={<OfferRatePage user={user} view={view} setView={setView} setSelectedCompany={setSelectedCompany} refreshSignal={reviews} />} />
          <Route path="/ghost-rate" element={<GhostRatePage user={user} view={view} setView={setView} setSelectedCompany={setSelectedCompany} refreshSignal={reviews} />} />
          <Route path="/timeline" element={<TimelinePage user={user} view={view} setView={setView} setSelectedCompany={setSelectedCompany} refreshSignal={reviews} />} />

          {/* Admin */}
          <Route path="/admin" element={<AdminPage user={user} />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>

      <Modal
        isOpen={isModalOpen}
        initialData={editingReview}
        onClose={() => setIsModalOpen(false)}
        onSuccess={(newReview) => {
          const reload = (view === 'my-reviews' || view === 'my-kahani') ? loadMyReviews : loadReviews;
          if (newReview) {
            if (editingReview) {
              setReviews(prev =>
                prev.map(r =>
                  (r._id || r.id) === (newReview._id || newReview.id) ? newReview : r
                )
              );
            } else {
              setReviews(prev => [newReview, ...prev]);
              setWidgetRefreshKey(k => k + 1);
              if (view === 'my-kahani') setLastSharedReview(newReview);
            }
          } else {
            reload();
            setWidgetRefreshKey(k => k + 1);
          }
        }}
      />

      {/* ── Guest Share Popup ── */}
      {showShareGuestPopup && (
        <div
          onClick={() => setShowShareGuestPopup(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 20, padding: '36px 32px',
              maxWidth: 380, width: '100%', textAlign: 'center',
              boxShadow: '0 24px 48px rgba(0,0,0,0.3)',
              border: '1px solid var(--border)',
              animation: 'fadeUp 0.25s ease-out',
            }}
          >
            <div style={{ fontSize: 44, marginBottom: 14 }}>✍️</div>
            <h2 style={{ fontFamily: 'var(--display)', fontSize: 20, fontWeight: 900, margin: '0 0 10px', color: 'var(--text)' }}>
              Sign in to share your Kahani
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 28px', lineHeight: 1.6 }}>
              Create a free account or sign in to contribute your interview experience to the community.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => { setShowShareGuestPopup(false); setUser(null); }}
                style={{
                  background: 'var(--blue)', color: '#fff', border: 'none',
                  padding: '13px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'var(--sans)',
                  boxShadow: '0 4px 12px rgba(14,165,233,0.3)',
                }}
              >
                Sign In / Sign Up
              </button>
              <button
                onClick={() => setShowShareGuestPopup(false)}
                style={{
                  background: 'transparent', color: 'var(--text3)',
                  border: '1px solid var(--border)',
                  padding: '12px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'var(--sans)',
                }}
              >
                Not Now — Continue as Guest
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Jobs Guest Popup ── */}
      {showJobsGuestPopup && (
        <div
          onClick={() => setShowJobsGuestPopup(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 20, padding: '36px 32px',
              maxWidth: 380, width: '100%', textAlign: 'center',
              boxShadow: '0 24px 48px rgba(0,0,0,0.3)',
              border: '1px solid var(--border)',
              animation: 'fadeUp 0.25s ease-out',
            }}
          >
            <div style={{ fontSize: 44, marginBottom: 14 }}>💼</div>
            <h2 style={{ fontFamily: 'var(--display)', fontSize: 20, fontWeight: 900, margin: '0 0 10px', color: 'var(--text)' }}>
              Sign in to view Jobs
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 28px', lineHeight: 1.6 }}>
              Create a free account or sign in to access job listings and stay updated on new openings.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => { setShowJobsGuestPopup(false); setUser(null); }}
                style={{
                  background: 'var(--blue)', color: '#fff', border: 'none',
                  padding: '13px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'var(--sans)',
                  boxShadow: '0 4px 12px rgba(14,165,233,0.3)',
                }}
              >
                Sign In / Sign Up
              </button>
              <button
                onClick={() => setShowJobsGuestPopup(false)}
                style={{
                  background: 'transparent', color: 'var(--text3)',
                  border: '1px solid var(--border)',
                  padding: '12px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'var(--sans)',
                }}
              >
                Not Now — Continue as Guest
              </button>
            </div>
          </div>
        </div>
      )}

      {showOnboarding && (
        <OnboardingModal
          user={user}
          onComplete={(nickname, startTour) => {
            if (nickname) {
              setUser(u => ({ ...u, nickname, displayName: nickname }));
              setFlyingName(nickname);
              setTimeout(() => setFlyingName(null), 1600);
            }
            setShowOnboarding(false);
            setShowWelcomePop(true);
            setTimeout(() => setShowWelcomePop(false), 7000);
            if (startTour) setTimeout(() => setShowTour(true), 500);
          }}
        />
      )}

      {/* Welcome popup for new users */}
      {showWelcomePop && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 5000, width: 'min(360px, calc(100vw - 32px))',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          padding: '16px 18px', display: 'flex', gap: 12, alignItems: 'flex-start',
          animation: 'welcomePopIn 0.4s cubic-bezier(0.16,1,0.3,1)',
        }}>
          <span style={{ fontSize: 28, flexShrink: 0 }}>🔒</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--text)', marginBottom: 4 }}>
              You're anonymous here
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
              HireKahani is built on honest, anonymous interview stories. Share your real experience — it helps thousands of job seekers make smarter career decisions.
            </div>
          </div>
          <button
            onClick={() => setShowWelcomePop(false)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text2)', fontSize: 16, padding: 0, flexShrink: 0, lineHeight: 1,
            }}
          >✕</button>
        </div>
      )}

      <style>{`
        @keyframes welcomePopIn {
          from { opacity: 0; transform: translateX(-50%) translateY(16px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      {/* Flying name animation */}
      {flyingName && (
        <div style={{
          position: 'fixed', zIndex: 4000, pointerEvents: 'none',
          top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          fontFamily: 'var(--display)', fontSize: 28, fontWeight: 900,
          color: '#0ea5e9', whiteSpace: 'nowrap',
          animation: 'flyToProfile 1.5s cubic-bezier(0.4,0,0.2,1) forwards',
        }}>
          {flyingName}
        </div>
      )}

      {/* ── Global Mobile Bottom Navigation ── */}
      <MobileBottomNav
        view={view}
        setView={guardedSetView}
        onHomeClick={handleHomeClick}
        onActivityClick={handleReviewClick}
        user={user}
        onSignInRequired={() => { setUser(null); setView('login'); }}
      />

      {showTour && <DashboardTour onClose={() => setShowTour(false)} />}

      {showLinkPhone && (
        <LinkPhoneModal
          onClose={() => setShowLinkPhone(false)}
          onLinked={() => setShowLinkPhone(false)}
        />
      )}

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes flyToProfile {
          0%   { top: 50%; left: 50%; transform: translate(-50%,-50%) scale(1);   opacity: 1; }
          60%  { top: 22px; left: calc(100vw - 120px); transform: translate(0,0) scale(0.65); opacity: 1; }
          100% { top: 22px; left: calc(100vw - 120px); transform: translate(0,0) scale(0.4);  opacity: 0; }
        }
        * { box-sizing: border-box; }
        body { margin: 0; background: var(--bg); font-family: var(--sans); }
      `}</style>
    </BrowserRouter>
  );
}

